import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Card, Modal, StatusBadge, Ico, Icons, PlayerAvatar, isPlayerRegistered, resolveTeam, ProfileSkeleton, Toast } from '../../components/common';
import { SVG_AVATARS } from '../../components/common/PlayerAvatar';
import { playerAPI } from '../../services/api';
import { formatDateIST } from '../../utils/dateUtils';

export default function Profile() {
  const { currentUser, logout } = useApp();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [shareMsg, setShareMsg] = useState(false);
  const [tab, setTab] = useState('timeline');
  const [ready, setReady] = useState(false);
  const [player, setPlayer] = useState(null);
  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    if (!currentUser?.playerId) {
      setReady(true);
      return;
    }
    const load = async () => {
      try {
        const res = await playerAPI.getDetails(currentUser.playerId);
        setPlayer(res.data.player);
        setMatches(res.data.matches);
        setTournaments(res.data.tournaments);
        setPlayers([res.data.player, ...res.data.relatedPlayers]);
        setReady(true);
      } catch (err) {
        console.error(err);
        setReady(true);
      }
    };
    load();
  }, [currentUser?.playerId]);
  const [editForm, setEditForm] = useState({ name: '', country: '', bio: '', photo: '', phone: '' });
  const [showAvatars, setShowAvatars] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  if (!ready) return <ProfileSkeleton />;

  if (!currentUser?.playerId) {
    return (
      <div className="container-sm">
        <div className="empty-state" style={{ marginTop: 24 }}>
          <div className="empty-icon">🔒</div>
          <h2 className="empty-title" style={{ fontSize: 18 }}>No Player Profile</h2>
          <p className="empty-sub">{currentUser?.role === 'organizer' ? 'Organizer accounts manage events instead.' : 'Account not linked to a player.'}</p>
          {currentUser?.role === 'organizer' && <Link to="/organizer" className="btn-primary" style={{ display: 'inline-block', marginTop: 14 }}>Go to My Events →</Link>}
        </div>
      </div>
    );
  }
  if (!player) return <ProfileSkeleton />;
  const pMatches = matches.filter(m => m.player1 && m.player2);
  const myT = tournaments.filter(t => isPlayerRegistered(player.id, t.registeredPlayers));
  const wr = player.matchesPlayed ? ((player.wins / player.matchesPlayed) * 100).toFixed(0) : 0;

  const openEdit = () => {
    setEditForm({ name: player.name, country: player.country || '', bio: player.bio || '', photo: player.photo || '', phone: player.phone || '' });
    setEditOpen(true);
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    const payload = { 
      name: editForm.name.trim(), 
      country: editForm.country.trim(), 
      bio: editForm.bio.trim(), 
      avatar: editForm.photo || null, 
      phone: editForm.phone.trim() || null 
    };
    try {
      await playerAPI.update(player.id, payload);
      setPlayer(p => ({ ...p, ...payload, photo: payload.avatar }));
      setEditOpen(false);
      setToast({ show: true, message: 'Profile updated successfully', type: 'success' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) { 
      setToast({ show: true, message: err.message || 'Failed to save', type: 'error' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    }
  };


  return (
    <div className="container-sm" style={{ padding: '0 16px 24px', overflow: 'hidden' }}>
      <Toast message={toast.message} type={toast.type} isVisible={toast.show} />
      {/* Hero */}
      <div className="hero-banner" style={{ background: 'linear-gradient(135deg, #00205B 60%, #0A2E6E)', borderRadius: '0 0 20px 20px', padding: '28px 24px', marginLeft: -16, marginRight: -16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, maxWidth: 868, margin: '0 auto' }}>
          <PlayerAvatar name={player.name} photo={player.photo} size={52} style={{ borderRadius: 14, border: '2px solid rgba(189,255,0,0.3)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="hero-title" style={{ fontSize: 20 }}>{player.name}</h1>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{player.country}</div>
            {player.bio && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4, lineHeight: 1.4 }}>{player.bio}</div>}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button onClick={openEdit} style={{ padding: '7px 10px', background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
              <Ico d={Icons.edit} size={14} color="#fff" />
            </button>
            <button onClick={() => {
              const url = `${window.location.origin}/players/${player.id}`;
              if (navigator.share) navigator.share({ title: player.name, url });
              else { navigator.clipboard.writeText(url); setShareMsg(true); setTimeout(() => setShareMsg(false), 2000); }
            }} style={{ padding: '7px 10px', background: shareMsg ? 'rgba(67,160,71,0.3)' : 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
              {shareMsg ? <Ico d={Icons.check} size={14} color="#BDFF00" /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ background: '#fff', borderRadius: 14, marginTop: -20, position: 'relative', zIndex: 1, boxShadow: '0 4px 16px rgba(0,32,91,0.06)', border: '1px solid #EEF1F6', padding: '16px 20px', marginBottom: 24, maxWidth: 868, marginLeft: 'auto', marginRight: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#8892A6', letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif" }}>{player.matchesPlayed} MATCHES</span>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif" }}>{wr}% WIN RATE</span>
        </div>
        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#EEF1F6' }}>
          {player.wins > 0 && <div style={{ flex: player.wins, background: '#43A047', transition: 'flex 0.4s' }} />}
          {player.losses > 0 && <div style={{ flex: player.losses, background: '#E53935', transition: 'flex 0.4s' }} />}
          {(player.matchesPlayed - player.wins - player.losses) > 0 && <div style={{ flex: player.matchesPlayed - player.wins - player.losses, background: '#D8DDE8', transition: 'flex 0.4s' }} />}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: '#43A047' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#43A047', fontFamily: "'Outfit', sans-serif" }}>{player.wins}</span>
            <span style={{ fontSize: 10, color: '#8892A6' }}>Won</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: '#E53935' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#E53935', fontFamily: "'Outfit', sans-serif" }}>{player.losses}</span>
            <span style={{ fontSize: 10, color: '#8892A6' }}>Lost</span>
          </div>
          {player.titles > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Ico d={Icons.trophy} size={11} color="#F9A825" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#F9A825', fontFamily: "'Outfit', sans-serif" }}>{player.titles}</span>
            <span style={{ fontSize: 10, color: '#8892A6' }}>{player.titles === 1 ? 'Title' : 'Titles'}</span>
          </div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#00205B', fontFamily: "'Outfit', sans-serif" }}>{player.points || 0}</span>
            <span style={{ fontSize: 10, color: '#8892A6' }}>Pts</span>
          </div>
        </div>
      </div>

      {(() => {
        const mw = { maxWidth: 900, marginLeft: 'auto', marginRight: 'auto' };
        const pid = player.id;
        const results = pMatches.filter(m => m.status === 'completed' || m.status === 'live');
        const upcoming = pMatches.filter(m => m.status === 'scheduled');
        const tabs = [
          { key: 'timeline', label: 'Timeline', count: results.length },
          { key: 'upcoming', label: 'Upcoming', count: upcoming.length },
          { key: 'results', label: 'Results', count: results.length },
        ];
        const visible = tab === 'results' ? results : tab === 'upcoming' ? upcoming : results;
        const renderCard = m => {
          const t = tournaments.find(t => t.id === m.tournamentId);
          const isDone = m.status === 'completed';
          const isLive = m.status === 'live';
          const inP1 = m.player1 && (m.player1 === pid || (m.player1.includes?.('+') && m.player1.split('+').includes(pid)));
          const isWin = isDone && m.winner && (m.winner === pid || (m.winner.includes?.('+') && m.winner.split('+').includes(pid)));
          const isLoss = isDone && m.winner && !isWin;
          const myScore = k => inP1 ? k.p1 : k.p2;
          const oppScore = k => inP1 ? k.p2 : k.p1;
          const opp = resolveTeam(inP1 ? m.player2 : m.player1, players);
          return (
            <div key={m.id} className="sch-card" style={{ cursor: 'pointer', ...(isLive ? { borderColor: '#E53935' } : {}) }} onClick={() => navigate(`/matches/${m.id}`)}>
              <div className="sch-card-top">
                <span className="sch-card-tourney">{t?.name} · {m.round}</span>
                {isDone ? (
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, padding: '3px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'Outfit', sans-serif", ...(isWin ? { background: '#E8F5E9', color: '#43A047' } : { background: '#FFEBEE', color: '#E53935' }) }}>
                    {isWin && <Ico d={Icons.trophy} size={11} color="#43A047" />}
                    {isWin ? 'WIN' : 'LOSS'}
                  </span>
                ) : <StatusBadge status={m.status} />}
              </div>
              <div className="sch-card-players">
                <div className="sch-card-row" style={{ background: isWin ? 'rgba(67,160,71,0.08)' : isLoss ? 'rgba(229,57,53,0.08)' : 'transparent', borderLeft: isWin ? '3px solid #43A047' : isLoss ? '3px solid #E53935' : '3px solid transparent' }}>
                  <PlayerAvatar name={resolveTeam(inP1 ? m.player1 : m.player2, players).name} photo={players.find(p => p.id === (inP1 ? m.player1 : m.player2))?.photo} size={28} />
                  {(() => { const pid2 = inP1 ? m.player1 : m.player2; const isDoubles = pid2?.includes?.('+'); const nameEl = resolveTeam(pid2, players).name; const st = { flex: '1 1 0%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, fontSize: 13, fontWeight: 700, color: isWin ? '#43A047' : isLoss ? '#E53935' : 'var(--navy)', textDecoration: 'none' }; return isDoubles ? <span style={st}>{nameEl}</span> : <Link to={`/players/${pid2}`} onClick={e => e.stopPropagation()} style={st}>{nameEl}</Link>; })()}
                  {m.score.sets.length > 0 && <div className="sch-card-scores">{m.score.sets.map((s, i) => <span key={i} className={`score-set ${myScore(s) > oppScore(s) ? 'score-set-win' : 'score-set-lose'}`}>{myScore(s)}</span>)}</div>}
                </div>
                <div className="sch-card-row" style={{ borderLeft: '3px solid transparent' }}>
                  <PlayerAvatar name={opp.name} photo={players.find(p => p.id === (inP1 ? m.player2 : m.player1))?.photo} size={28} />
                  {(() => { const oid = inP1 ? m.player2 : m.player1; const isDoubles = oid?.includes?.('+'); const st = { flex: '1 1 0%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, fontSize: 13, fontWeight: 500, color: 'var(--gray-400)', textDecoration: 'none' }; return isDoubles ? <span style={st}>{opp.name}</span> : <Link to={`/players/${oid}`} onClick={e => e.stopPropagation()} style={st}>{opp.name}</Link>; })()}
                  {m.score.sets.length > 0 && <div className="sch-card-scores">{m.score.sets.map((s, i) => <span key={i} className={`score-set ${oppScore(s) > myScore(s) ? 'score-set-win' : 'score-set-lose'}`}>{oppScore(s)}</span>)}</div>}
                </div>
              </div>
              <div className="sch-card-bottom">
                <span className={`sch-card-time ${isLive ? 'sch-card-time-live' : ''}`}>
                  {isLive ? '● LIVE' : m.time || '—'}
                </span>
                {m.date && <span className="chip-sm"><Ico d={Icons.calendar} size={10} color="#8892A6" />{new Date(m.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                {m.court && <span className="sch-card-court">{m.court}</span>}
              </div>
            </div>
          );
        };
        return <div style={mw}>
          <div style={{ display: 'flex', gap: 8, marginTop: 28, marginBottom: 12 }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '6px 16px', borderRadius: 20, border: tab === t.key ? '1.5px solid #00205B' : '1.5px solid #EEF1F6', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Outfit', sans-serif", letterSpacing: 0.5, transition: 'all 0.2s', whiteSpace: 'nowrap', background: tab === t.key ? 'rgba(0,32,91,0.06)' : 'var(--card-bg, #fff)', color: tab === t.key ? '#00205B' : '#8892A6' }}>
                {t.label} ({t.count})
              </button>
            ))}
          </div>
          {tab === 'timeline' ? (() => {
            const completed = results.filter(m => m.status === 'completed' && m.winner);
            const sorted = [...completed].sort((a, b) => (b.date || b.updatedAt || '') > (a.date || a.updatedAt || '') ? 1 : -1);
            const streak = sorted.slice(0, 20).map(m => {
              const isWin = m.winner === pid || (m.winner?.includes?.('+') && m.winner.split('+').includes(pid));
              return isWin ? 'W' : 'L';
            });
            const currentStreak = streak.length > 0 ? (() => {
              const first = streak[0]; let count = 0;
              for (const r of streak) { if (r === first) count++; else break; }
              return { type: first, count };
            })() : null;
            const grouped = []; let lastTid = null;
            sorted.forEach(m => {
              if (m.tournamentId !== lastTid) { grouped.push({ tournament: tournaments.find(t => t.id === m.tournamentId), matches: [m] }); lastTid = m.tournamentId; }
              else grouped[grouped.length - 1].matches.push(m);
            });
            return sorted.length === 0 ? <Card><p style={{ color: '#8892A6' }}>No match history yet</p></Card> : (
              <div>
                <div style={{ background: 'var(--card-bg)', borderRadius: 12, border: '1px solid var(--card-border)', padding: '14px 16px', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--gray-400)', letterSpacing: 1.5, fontFamily: "var(--font-display)" }}>RECENT FORM</span>
                    {currentStreak && <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 6, fontFamily: "var(--font-display)", letterSpacing: 0.5, background: currentStreak.type === 'W' ? '#E8F5E9' : '#FFEBEE', color: currentStreak.type === 'W' ? '#43A047' : '#E53935' }}>{currentStreak.count} {currentStreak.type === 'W' ? 'WIN' : 'LOSS'} STREAK</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 4, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 2 }}>
                    {streak.map((r, i) => <div key={i} style={{ width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, fontFamily: "var(--font-display)", flexShrink: 0, background: r === 'W' ? '#43A047' : '#E53935', color: '#fff', opacity: 1 - (i * 0.03) }}>{r}</div>)}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                    <span style={{ fontSize: 10, color: 'var(--gray-300)' }}>← Most recent</span>
                    <span style={{ fontSize: 10, color: 'var(--gray-300)', marginLeft: 'auto' }}>{streak.filter(r => r === 'W').length}W {streak.filter(r => r === 'L').length}L in last {streak.length}</span>
                  </div>
                </div>
                {grouped.map((g, gi) => (
                  <div key={gi} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--navy)', flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', fontFamily: "var(--font-display)", letterSpacing: 0.3 }}>{g.tournament?.name || 'Unknown Tournament'}</span>
                    </div>
                    <div style={{ marginLeft: 3, borderLeft: '2px solid var(--gray-100)', paddingLeft: 16 }}>
                      {g.matches.map(m => {
                        const inP1 = m.player1 && (m.player1 === pid || (m.player1.includes?.('+') && m.player1.split('+').includes(pid)));
                        const isWin = m.winner && (m.winner === pid || (m.winner.includes?.('+') && m.winner.split('+').includes(pid)));
                        const opp = resolveTeam(inP1 ? m.player2 : m.player1, players);
                        const myS = k => inP1 ? k.p1 : k.p2;
                        const oppS = k => inP1 ? k.p2 : k.p1;
                        return (
                          <Link to={`/matches/${m.id}`} key={m.id} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 6, borderRadius: 10, background: 'var(--card-bg)', border: '1px solid var(--card-border)', position: 'relative' }}>
                            <div style={{ position: 'absolute', left: -25, width: 12, height: 12, borderRadius: '50%', border: '2px solid ' + (isWin ? '#43A047' : '#E53935'), background: 'var(--bg)' }} />
                            <div style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: isWin ? '#E8F5E9' : '#FFEBEE' }}>
                              {isWin ? <Ico d={Icons.trophy} size={14} color="#43A047" /> : <span style={{ fontSize: 14, fontWeight: 800, color: '#E53935', fontFamily: "var(--font-display)" }}>L</span>}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: isWin ? '#43A047' : '#E53935', fontFamily: "var(--font-display)" }}>{isWin ? 'W' : 'L'}</span>
                                <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>vs</span>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "var(--font-display)" }}>{opp.name || 'TBD'}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                <span style={{ fontSize: 10, color: 'var(--gray-300)' }}>{m.round}</span>
                                {m.date && <><span style={{ fontSize: 10, color: 'var(--gray-200)' }}>·</span><span style={{ fontSize: 10, color: 'var(--gray-300)' }}>{formatDateIST(m.date, { month: 'short', day: 'numeric' })}</span></>}
                                {m.specialResult && <><span style={{ fontSize: 10, color: 'var(--gray-200)' }}>·</span><span style={{ fontSize: 10, color: '#E53935', fontWeight: 600 }}>{m.specialResult === 'walkover' ? 'W/O' : m.specialResult === 'retirement' ? 'RET' : 'DQ'}</span></>}
                              </div>
                            </div>
                            {m.score?.sets?.length > 0 && <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                              {m.score.sets.map((s, si) => { const won = myS(s) > oppS(s); return <span key={si} style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: won ? 700 : 400, fontFamily: "var(--font-display)", color: won ? 'var(--navy)' : 'var(--gray-300)', background: won ? 'rgba(0,32,91,0.04)' : 'transparent', borderRadius: 4 }}>{myS(s)}-{oppS(s)}</span>; })}
                            </div>}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })() : visible.length === 0 ? <Card><p style={{ color: '#8892A6' }}>{tab === 'results' ? 'No results yet' : 'No upcoming matches'}</p></Card>
            : <div className="mh-grid">{visible.map(renderCard)}</div>}
        </div>;
      })()}

      <div style={{ maxWidth: 900, marginLeft: 'auto', marginRight: 'auto' }}>
      <h2 className="heading" style={{ marginTop: 28, marginBottom: 12 }}>MY TOURNAMENTS</h2>
      {myT.length === 0 ? (
        <div className="empty-state" style={{ padding: 20 }}><p className="empty-sub">No tournaments yet. <Link to="/tournaments" style={{ color: '#1E88E5', fontWeight: 600 }}>Browse →</Link></p></div>
      ) : (
        <div className="grid-cards" style={{ marginBottom: 8 }}>
          {myT.map(t => {
  const surfaceColors = { 'Hard Court': '#1E88E5', 'Clay': '#ffb68f', 'Grass': '#43A047' };
            const surfaceColor = surfaceColors[t.surface] || '#8892A6';
            return (
              <Link to={`/tournaments/${t.id}`} key={t.id} style={{ textDecoration: 'none' }}>
                <div className="t-card" style={{ height: '100%' }}>
                  <div className="t-header" style={{ background: `linear-gradient(135deg, #2B4F8A 30%, ${surfaceColor})`, padding: '12px 14px 10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <StatusBadge status={t.status} />
                      <span className="t-surface-tag">{t.surface}</span>
                    </div>
                    <h3 className="t-name" style={{ marginTop: 8 }}>{t.name}</h3>
                  </div>
                  <div style={{ padding: '10px 14px 12px', display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8892A6' }}>
                    <span><Ico d={Icons.pin} size={11} color="#8892A6" /> {t.location}</span>
                    <span>{t.type}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      </div>

      {/* Edit Profile Modal */}
      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title="Edit Profile">
        <form onSubmit={saveEdit}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <PlayerAvatar name={editForm.name || player.name} photo={editForm.photo || null} size={64} />
              {editForm.photo && <button type="button" onClick={() => setEditForm(p => ({ ...p, photo: '' }))} style={{ position: 'absolute', top: -4, right: -4, width: 20, height: 20, borderRadius: '50%', background: '#E53935', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>}
            </div>
          </div>
          <label className="form-label">Profile Avatar</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <button type="button" onClick={() => setShowAvatars(!showAvatars)} style={{ flex: 1, padding: '10px 14px', background: showAvatars ? '#00205B' : 'var(--gray-50)', color: showAvatars ? '#BDFF00' : 'var(--gray-500)', border: '1.5px solid var(--gray-200)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Pick Avatar</button>
          </div>
          {showAvatars && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12, padding: 12, background: 'var(--gray-50)', borderRadius: 10, border: '1px solid var(--gray-100)' }}>
              {SVG_AVATARS.map(av => (
                <button type="button" key={av.id} onClick={() => { setEditForm(p => ({ ...p, photo: av.id })); setShowAvatars(false); }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 8, borderRadius: 10, border: editForm.photo === av.id ? '2px solid #00205B' : '1px solid var(--gray-200)', background: editForm.photo === av.id ? '#E3F2FD' : '#fff', cursor: 'pointer' }}>
                  <PlayerAvatar photo={av.id} name="" size={36} />
                  <span style={{ fontSize: 9, color: '#5F6B80', fontWeight: 600 }}>{av.label}</span>
                </button>
              ))}
            </div>
          )}
          <label className="form-label">Name</label>
          <input className="form-input" style={{ marginBottom: 10 }} value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} required />
          <label className="form-label">Country</label>
          <input className="form-input" style={{ marginBottom: 10 }} value={editForm.country} onChange={e => setEditForm(p => ({ ...p, country: e.target.value }))} placeholder="e.g. India" />
          <label className="form-label">Phone</label>
          <input className="form-input" style={{ marginBottom: 10 }} value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} placeholder="e.g. 9876543210" />
          <label className="form-label">Bio</label>
          <textarea className="form-input" style={{ marginBottom: 16, minHeight: 60 }} value={editForm.bio} onChange={e => setEditForm(p => ({ ...p, bio: e.target.value }))} placeholder="Tell something about yourself..." />
          <button type="submit" className="btn-primary" style={{ width: '100%', padding: 12 }}>Save Changes</button>
        </form>
      </Modal>
    </div>
  );
}
